/**
 * Hourly Activity Chart Widget
 *
 * Displays activity trends over time using a bar chart.
 * Shows picks, receives, and shipments per hour.
 * Connected to live Dashboard API with mock fallback.
 *
 * @author BizClick WMS
 * @version 2.0.0
 */

import React from 'react';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  Legend,
} from 'recharts';
import { WidgetInstance, HourlyActivity } from '../../types';
import { getHourlyActivity } from '../../mockDataService';
import { fetchHourlyActivity } from '../../../../../../services/dashboardService';
import { useWidgetData } from '../../hooks/useWidgetData';
import WidgetWrapper from '../WidgetWrapper';
import { WidgetSkeleton } from '../shared';

interface HourlyActivityChartWidgetProps {
  instance: WidgetInstance;
}

const HourlyActivityChartWidget: React.FC<HourlyActivityChartWidgetProps> = ({ instance }) => {
  const { data, isLoading, error, refresh, isFirstLoad, isLive } = useWidgetData<HourlyActivity[]>({
    widgetId: 'hourly-activity',
    instance,
    fetchFn: async () => {
      try {
        return await fetchHourlyActivity();
      } catch {
        return getHourlyActivity();
      }
    },
  });

  if (isFirstLoad) {
    return (
      <WidgetWrapper instance={instance} onRefresh={refresh} isLoading={true}>
        <WidgetSkeleton variant="chart" />
      </WidgetWrapper>
    );
  }

  const chartData = data ?? [];

  const CustomTooltip = ({ active, payload, label }: any) => {
    if (active && payload && payload.length) {
      return (
        <div className="bg-gray-800 border border-gray-700 rounded-lg p-2 shadow-lg">
          <p className="text-white font-medium mb-1">{label}</p>
          {payload.map((entry: any, index: number) => (
            <p key={index} className="text-sm" style={{ color: entry.color }}>
              {entry.name}: {entry.value}
            </p>
          ))}
        </div>
      );
    }
    return null;
  };

  return (
    <WidgetWrapper instance={instance} onRefresh={refresh} isLoading={isLoading} error={error?.message} isLive={isLive}>
      <div className="h-full w-full">
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={chartData} margin={{ top: 5, right: 5, left: -20, bottom: 5 }}>
            <XAxis
              dataKey="hour"
              axisLine={false}
              tickLine={false}
              tick={{ fill: '#9CA3AF', fontSize: 11 }}
            />
            <YAxis
              axisLine={false}
              tickLine={false}
              tick={{ fill: '#9CA3AF', fontSize: 11 }}
            />
            <Tooltip content={<CustomTooltip />} cursor={{ fill: 'rgba(255,255,255,0.05)' }} />
            <Legend
              wrapperStyle={{ fontSize: '11px', paddingTop: '5px' }}
              formatter={(value) => <span style={{ color: '#9CA3AF' }}>{value}</span>}
            />
            <Bar
              dataKey="picks"
              name="Plukk"
              fill="#22C55E"
              radius={[2, 2, 0, 0]}
              maxBarSize={30}
            />
            <Bar
              dataKey="receives"
              name="Mottak"
              fill="#3B82F6"
              radius={[2, 2, 0, 0]}
              maxBarSize={30}
            />
            <Bar
              dataKey="shipments"
              name="Utsending"
              fill="#06B6D4"
              radius={[2, 2, 0, 0]}
              maxBarSize={30}
            />
          </BarChart>
        </ResponsiveContainer>
      </div>
    </WidgetWrapper>
  );
};

export default HourlyActivityChartWidget;
